import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const SocialMediaLinkType = () => {
  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchList = () => {
    apiGet("SocialMediaLinkType/all")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load social media link types"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      socialMediaLinkTypeTitle: values.socialMediaLinkTypeTitle,
      socialMediaIcon: values.socialMediaIcon,
    };

    apiPost("SocialMediaLinkType", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    const payload = {
       id: values.id,
      socialMediaLinkTypeId: values.socialMediaLinkTypeId,
      socialMediaLinkTypeTitle: values.socialMediaLinkTypeTitle,
      socialMediaIcon: values.socialMediaIcon,
    };

    apiPut("SocialMediaLinkType", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`SocialMediaLinkType/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    socialMediaLinkTypeTitle: Yup.string().required("Title is required"),
    socialMediaIcon: Yup.string().required("Icon is required"),
  });

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
        <Row className="mb20">
          <Col xl="12">
            <Breadcrumbs
              title={"General Settings"}
              breadcrumbItem={"Social Media Link Type"}
            />
          </Col>
        </Row>
      <Row>
        <Col xl="12">
          {/* ===== ADD FORM ===== */}
          <Card>
            <CardBody>
              

              <Formik
                initialValues={{
                  socialMediaLinkTypeTitle: "",
                  socialMediaIcon: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>Title *</Label>
                          <SafeField
                            name="socialMediaLinkTypeTitle"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.socialMediaLinkTypeTitle &&
                              touched.socialMediaLinkTypeTitle
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="socialMediaLinkTypeTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Icon Class *</Label>
                          <SafeField
                            name="socialMediaIcon"
                            placeholder="fa fa-facebook"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.socialMediaIcon && touched.socialMediaIcon
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="socialMediaIcon"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2" className="">
                        <Button type="submit" color="primary" className="mt-4">
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ===== LIST ===== */}
          <Card>
            <CardBody>
              <h5>Social Media Types</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Icon</th>
                     
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.socialMediaLinkTypeId || item.id || i}>
                        <td>{i + 1}</td>
                        <td>{item.socialMediaLinkTypeTitle}</td>
                        <td>{item.socialMediaIcon}</td>
                       
                        <td>
                            <ActionEdit onClick={() => openEdit(item)} />{" "}
                            <ActionDelete onClick={() => handleDelete(item.id)} />
                          
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== EDIT OFFCANVAS ===== */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Social Media Type</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ handleChange }) => (
              <Form>
                <FormGroup>
                  <Label>Title *</Label>
                  <SafeField
                    name="socialMediaLinkTypeTitle"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Icon Class *</Label>
                  <SafeField
                    name="socialMediaIcon"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default SocialMediaLinkType;
